// Normalises AOI geometry so the rest of the app only ever handles Polygon features.
//
// True Sight requires `geometry.type === 'Polygon'` and rejects MultiPolygon outright, but checkout
// charges before the AOI is submitted for processing — so a MultiPolygon that survives this far is
// paid for and then refused by the engine. Splitting on arrival keeps the two in agreement: a
// single-part MultiPolygon becomes one Polygon and stays eligible, while a multi-part one becomes
// several features and is caught by the existing single-site rule.

const isMultiPolygon = (feature: GeoJSON.Feature): boolean =>
  (feature?.geometry as GeoJSON.Geometry)?.type === 'MultiPolygon';

// One Polygon feature per part. Each entry in a MultiPolygon's coordinates is a whole polygon
// including its interior rings, so holes survive and the area is unchanged. Properties are copied
// onto every part; `id` and `bbox` are deliberately not carried over — a duplicated `id` collides in
// mapbox-gl-draw, which keys features by it, and the parent's bbox does not describe a single part.
const toPolygonFeatures = (feature: GeoJSON.Feature): GeoJSON.Feature[] => {
  if (!isMultiPolygon(feature)) {
    return [feature];
  }

  const { coordinates } = feature.geometry as GeoJSON.MultiPolygon;
  if (!Array.isArray(coordinates)) {
    return [feature];
  }

  const parts = coordinates
    .filter((polygon) => Array.isArray(polygon) && polygon.length > 0)
    .map(
      (polygon): GeoJSON.Feature => ({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: polygon },
        properties: { ...(feature.properties || {}) },
      }),
    );

  // A MultiPolygon with no usable parts is left alone rather than silently dropped, so the existing
  // empty-geometry handling upstream stays in charge of it.
  return parts.length ? parts : [feature];
};

// The collection is spread rather than rebuilt: `crs` is read by coordinate.utils.ts's CRS detection
// and `stats` by the results layers, and both travel as top-level keys on the FeatureCollection. This
// is also why `turf.flatten` is not used here, besides it splitting multi-point and multi-line
// geometry that this rule has nothing to say about.
const toSinglePolygons = (geoJson: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection => {
  if (!Array.isArray(geoJson?.features) || !geoJson.features.some(isMultiPolygon)) {
    return geoJson;
  }

  return { ...geoJson, features: geoJson.features.flatMap(toPolygonFeatures) };
};

export const geometryUtils = {
  toSinglePolygons,
};
